import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface SegmentedControlProps {
  options: { value: string; label: ReactNode }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'default' | 'compact';
  className?: string;
  /**
   * Accessible name for the radiogroup. Required for SR users so the group
   * announces its purpose ("Surcharge Period", "Premium Type", etc.) before
   * reading the individual options. Pass either a plain string via
   * `ariaLabel` or an id via `ariaLabelledBy` that points at a visible label.
   */
  ariaLabel?: string;
  ariaLabelledBy?: string;
}

export function SegmentedControl({
  options,
  value,
  onChange,
  disabled = false,
  size = 'default',
  className,
  ariaLabel,
  ariaLabelledBy,
}: SegmentedControlProps) {
  const activeIndex = Math.max(0, options.findIndex((o) => o.value === value));
  const indicatorStyle = options.length > 0
    ? {
        left: '1px',
        width: `calc((100% - 2px) / ${options.length})`,
        transform: `translateX(${activeIndex * 100}%)`,
        transition: 'transform 200ms ease-out, width 200ms ease-out',
      }
    : null;

  return (
    <div
      className={cn(
        'relative inline-flex w-full rounded p-1 border border-border',
        'bg-surface-2 shadow-[inset_0_2px_4px_rgba(0,0,0,0.35)]',
        disabled && 'opacity-50 pointer-events-none',
        className
      )}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {indicatorStyle && (
        <div
          className="absolute top-1 bottom-1 left-0 rounded-[3px] bg-surface-1 border border-border shadow-[0_1px_3px_rgba(0,0,0,0.25)] [border-top-color:rgba(255,255,255,0.08)] will-change-transform"
          style={indicatorStyle}
        />
      )}

      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(option.value)}
            disabled={disabled}
            className={cn(
              'relative z-10 flex-1 flex items-center justify-center font-medium transition-all duration-150 cursor-pointer rounded-[3px]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-inset',
              'active:translate-y-[0.5px]',
              size === 'default' && 'text-[11px] sm:text-[12px] px-3 py-2 sm:py-1.5',
              size === 'sm' && 'text-[11px] sm:text-[12px] px-2 py-2 sm:py-1.5',
              size === 'compact' && 'min-h-9 text-[11px] sm:text-[12px] px-3',
              // Invisible 44px touch target on mobile without inflating visual height
              'after:absolute after:inset-x-0 after:-inset-y-1 after:min-h-[44px] after:content-[\'\'] sm:after:hidden',
              isActive
                ? 'text-primary-text font-semibold'
                : 'text-foreground/80 hover:text-foreground'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
